
import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Home, 
  Stethoscope, 
  Monitor, 
  BookOpen, 
  Phone,
  ChevronDown,
  Menu,
  X,
  ArrowRight
} from "lucide-react";
import { mainNavigation } from "@/config/navigation";
import scienzaLogo from "@/assets/scienza-logo.png";

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [pulseDemo, setPulseDemo] = useState(false);
  const location = useLocation();

  // Define pages with light backgrounds that need dark text
  const lightBackgroundPages = [
    '/resources',
    '/resources/white-papers-research',
    '/resources/case-studies',
    '/resources/blog-news',
    '/resources/compliance-security',
    '/contact-request-demo',
    '/about',
    '/about/our-story',
    '/about/team',
    '/about/careers'
  ];

  // Check if current page has light background
  const hasLightBackground = lightBackgroundPages.some(page => 
    location.pathname === page || location.pathname.startsWith(page + '/')
  );

  // Handle scroll for header background transition
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Pulse animation for demo button every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setPulseDemo(true);
      setTimeout(() => setPulseDemo(false), 1000);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  const handleDropdownHover = useCallback((itemTitle: string | null) => {
    setActiveDropdown(itemTitle);
  }, []);

  // Navigation icons mapping
  const navIcons = {
    "Solutions": Stethoscope,
    "Platform & Devices": Monitor,
    "Resources": BookOpen,
    "Contact": Phone
  };

  const getNavIcon = (title: string) => {
    const IconComponent = navIcons[title as keyof typeof navIcons] || Home;
    return <IconComponent className="w-4 h-4" />;
  };

  // Create navigation structure with consistent types
  type NavigationItem = {
    title: string;
    path: string;
    children?: Array<{
      title: string;
      path: string;
      children?: Array<{
        title: string;
        path: string;
      }>;
    }>;
  };

  const navigationItems: NavigationItem[] = [
    { title: "Home", path: "/" },
    ...mainNavigation.filter(item => ["Solutions", "Platform & Devices", "Resources", "Contact"].includes(item.title))
  ];

  return (
    <>
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-background/95 backdrop-blur-md border-b border-border/50 shadow-sm' 
            : 'bg-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 group">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-accent/20 rounded-lg blur-md group-hover:blur-lg transition-all duration-300"></div>
                <div className="relative z-10 transform group-hover:scale-105 transition-transform duration-300">
                  <div className="h-[80px] sm:h-[90px] md:h-[100px] w-full max-w-[400px] sm:max-w-[450px] md:max-w-[500px] relative">
                    <img 
                      src="/lovable-uploads/9cd5085a-fd47-4da4-be30-2d118cce6c09.png"
                      alt="Scienza Health Logo"
                      className="h-full w-auto object-contain"
                      loading="eager"
                      decoding="async"
                      width={500}
                      height={100}
                    />
                  </div>
                </div>
              </motion.div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center justify-center flex-1 space-x-8">
              {navigationItems.slice(0, 4).map((item) => {
                return (
                  <div
                    key={item.title}
                    className="relative group"
                    onMouseEnter={() => handleDropdownHover(item.children ? item.title : null)}
                    onMouseLeave={() => handleDropdownHover(null)}
                  >
                    <Link
                      to={item.path}
                      className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium transition-colors relative group ${
                        isScrolled 
                          ? 'text-foreground/80 hover:text-foreground' 
                          : hasLightBackground 
                            ? 'text-foreground/90 hover:text-foreground'
                            : 'text-white/80 hover:text-white'
                      }`}
                    >
                      {item.title !== "Home" && getNavIcon(item.title)}
                      {item.title === "Home" && <Home className="w-4 h-4" />}
                      <span>{item.title}</span>
                      {item.children && (
                        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${
                          activeDropdown === item.title ? 'rotate-180' : ''
                        }`} />
                      )}
                      
                      {/* Animated underline */}
                      <motion.div
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary origin-left"
                        initial={{ scaleX: 0 }}
                        whileHover={{ scaleX: 1 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                      />
                    </Link>

                    {/* Dropdown Menu */}
                    <AnimatePresence>
                      {item.children && activeDropdown === item.title && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 mt-2 w-64 bg-background/95 backdrop-blur-md border border-border/50 rounded-lg shadow-lg py-2 z-50"
                        >
                          {item.children.map((child) => (
                            <div key={child.title}>
                              <Link
                                to={child.path}
                                className="block px-4 py-2 text-sm text-foreground/80 hover:text-foreground hover:bg-muted/50 transition-colors"
                              >
                                {child.title}
                              </Link>
                              {child.children && (
                                <div className="pl-4 border-l border-border/30 ml-4">
                                  {child.children.map((grandchild) => (
                                    <Link
                                      key={grandchild.title}
                                      to={grandchild.path}
                                      className="block px-4 py-1.5 text-xs text-foreground/60 hover:text-foreground/80 transition-colors"
                                    >
                                      {grandchild.title}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>

            {/* Request Demo Button */}
            <motion.div
              animate={pulseDemo ? { scale: [1, 1.05, 1] } : {}}
              transition={{ duration: 1, ease: "easeInOut" }}
            >
            <button
              onClick={() => window.open('https://calendly.com/david-scienzahealth/product-demo', '_blank')}
              className="hidden lg:inline-flex items-center space-x-2 bg-primary text-primary-foreground px-6 py-2 rounded-full text-sm font-medium hover:bg-primary/90 transition-colors shadow-md hover:shadow-lg"
            >
              <Phone className="w-4 h-4" />
              <span>Request Demo</span>
            </button>
            </motion.div>

            {/* Mobile Menu Button */}
            <motion.button
              onClick={toggleMobileMenu}
              className={`lg:hidden p-2 rounded-lg transition-colors relative z-10 ${
                isScrolled 
                  ? 'text-foreground hover:text-foreground hover:bg-muted/50' 
                  : hasLightBackground 
                    ? 'text-foreground bg-background/80 backdrop-blur-sm border border-border/20 hover:bg-background hover:border-border/40 shadow-sm'
                    : 'text-white bg-white/10 backdrop-blur-sm hover:bg-white/20 border border-white/20'
              }`}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                animate={{ rotate: isMobileMenuOpen ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </motion.div>
            </motion.button>
          </div>
        </nav>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-background/80 backdrop-blur-md"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            
            {/* Menu Panel */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="absolute right-0 top-16 bottom-0 w-80 bg-background border-l border-border/50 shadow-2xl overflow-y-auto"
            >
              <div className="p-6 space-y-6">
                {navigationItems.map((item) => {
                  return (
                    <div key={item.title} className="space-y-3">
                      <Link
                        to={item.path}
                        className="flex items-center space-x-3 text-lg font-medium text-foreground hover:text-primary transition-colors"
                      >
                        {item.title !== "Home" && getNavIcon(item.title)}
                        {item.title === "Home" && <Home className="w-5 h-5" />}
                        <span>{item.title}</span>
                      </Link>
                      
                      {item.children && (
                        <div className="pl-8 space-y-2 border-l border-border/30">
                          {item.children.map((child) => (
                            <div key={child.title} className="space-y-2">
                              <Link
                                to={child.path}
                                className="block text-sm text-foreground/80 hover:text-foreground transition-colors"
                              >
                                {child.title}
                              </Link>
                              {child.children && (
                                <div className="pl-4 space-y-1 border-l border-border/20">
                                  {child.children.map((grandchild) => (
                                    <Link
                                      key={grandchild.title}
                                      to={grandchild.path}
                                      className="flex items-center text-xs text-foreground/60 hover:text-foreground/80 transition-colors"
                                    >
                                      <ArrowRight className="w-3 h-3 mr-2" />
                                      {grandchild.title}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {/* Mobile Demo Button */}
                <motion.div
                  animate={pulseDemo ? { scale: [1, 1.05, 1] } : {}}
                  transition={{ duration: 1, ease: "easeInOut" }}
                  className="pt-6 border-t border-border/30"
                >
                  <button
                    onClick={() => window.open('https://calendly.com/david-scienzahealth/product-demo', '_blank')}
                    className="flex items-center justify-center space-x-2 bg-primary text-primary-foreground px-6 py-3 rounded-full text-sm font-medium hover:bg-primary/90 transition-colors shadow-md"
                  >
                    <Phone className="w-4 h-4" />
                    <span>Request Demo</span>
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
